import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Trash2, Edit3, Loader2, Save, X, RotateCcw, KeyRound, Building2, Copy, LayoutGrid, Image as ImageIcon } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import MobileListCard from '../components/MobileListCard';
import { useAdminAuth, API } from '../context/AdminAuthContext';
import { useToast } from '../hooks/use-toast';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import ImageUpload, { resolveImageUrl } from '../components/ImageUpload';

/**
 * AdminBranches — super-admin page to create + manage branch portal
 * accounts. Each branch is linked to one or more customer_numbers,
 * and every order carrying one of those numbers is visible in that
 * branch's /branch/orders view.
 *
 * Route: /admin/branches
 */

// Feb 2026 — Per-weekday duty schedule. Mon=0 … Sun=6.
const WEEKDAY_META = [
  { v: 0, l: 'Mon' },
  { v: 1, l: 'Tue' },
  { v: 2, l: 'Wed' },
  { v: 3, l: 'Thu' },
  { v: 4, l: 'Fri' },
  { v: 5, l: 'Sat' },
  { v: 6, l: 'Sun' },
];

const BLANK_DAY = { start: '', end: '', off: false };
const blankWeek = () => Array.from({ length: 7 }, () => ({ ...BLANK_DAY }));

// Client mirror of services/duty_schedule.derive_duty_schedule so a
// branch record with only legacy fields renders a populated grid.
const deriveDutySchedule = (rec) => {
  if (!rec || typeof rec !== 'object') return blankWeek();
  const raw = rec.duty_schedule;
  if (Array.isArray(raw) && raw.length === 7) {
    return raw.map((e) => ({
      start: typeof e?.start === 'string' ? e.start : '',
      end: typeof e?.end === 'string' ? e.end : '',
      off: !!e?.off,
    }));
  }
  const ds = (rec.duty_start || '').trim();
  const de = (rec.duty_end || '').trim();
  const dd = Array.isArray(rec.duty_days) ? rec.duty_days.map(Number) : [];
  const od = Array.isArray(rec.off_duty_days) ? rec.off_duty_days.map(Number) : [];
  const dutyDaysConfigured = dd.length > 0;
  return Array.from({ length: 7 }, (_, w) => {
    if (od.includes(w)) return { start: '', end: '', off: true };
    if (dutyDaysConfigured && !dd.includes(w)) return { start: '', end: '', off: true };
    return { start: ds, end: de, off: false };
  });
};


const empty = {
  username: '',
  name: '',
  emails: [],
  emailDraft: '',
  password: '',
  customer_numbers_text: '',
  allowed_category_slugs: [],
  active: true,
  // Feb 2026 — Duty hours mirror admin_users. Empty = no window.
  duty_start: '',
  duty_end: '',
  duty_days: [],
  off_duty_days: [],
  // Feb 2026 — Per-weekday duty schedule. 7 entries indexed by
  // Python weekday (0=Mon..6=Sun). Each entry:
  // { start: 'HH:MM' | '', end: 'HH:MM' | '', off: bool }.
  duty_schedule: [
    { start: '', end: '', off: false },
    { start: '', end: '', off: false },
    { start: '', end: '', off: false },
    { start: '', end: '', off: false },
    { start: '', end: '', off: false },
    { start: '', end: '', off: false },
    { start: '', end: '', off: false },
  ],
  // Feb 2026 — Branch FEFO WMS. Warehouse binding. Empty = unbound.
  warehouse_id: '',
  // Feb 2026 — Branch FEFO WMS · Phase 3. Explicit opt-in for
  // receiving + live-stock sidebar tabs.
  has_receiving: false,
};

// Reuse the exact parsing rule from ManualRecipientsModal so admin-side
// UX stays consistent: comma / semicolon / whitespace separated,
// case-normalised, de-duplicated.
const parseEmails = (draft) => (draft || '')
  .split(/[,;\s]+/)
  .map((s) => s.trim().toLowerCase())
  .filter((s) => s.length > 0);

const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((e || '').trim());


// ---------------------------------------------------------------------
// Document visibility panel (Feb 2026)
// ---------------------------------------------------------------------
// Super-admin toggles who on the /branch portal can see the receipt
// documents uploaded against orders.
//   * Global switch — off = nobody outside admin sees the panel.
//   * Per-user allow-list — when the global switch is ON, the checked
//     branch users see documents; unchecked users don't.
//   * Empty allow-list (backwards compat) = everyone sees documents.
// Admin sees the upload preview panel on Admin Orders regardless of
// this setting.
const DocumentVisibilityPanel = ({ branches }) => {
  const { authHeaders } = useAdminAuth();
  const { toast } = useToast();
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/branch-portal-settings`, { headers: authHeaders })
      .then(({ data }) => setCfg(data))
      .catch(() => setCfg({ show_received_documents: true, document_visibility_user_ids: [] }));
  }, [authHeaders]);

  const save = async (next) => {
    setBusy(true);
    try {
      const { data } = await axios.put(`${API}/admin/branch-portal-settings`, {
        // Merge existing config with the change so we don't clobber
        // sibling settings (hidden_category_slugs, available_qty_visible,
        // and the loading-photos visibility pair added Feb 2026).
        hidden_category_slugs: cfg?.hidden_category_slugs || [],
        available_qty_visible: cfg?.available_qty_visible ?? true,
        show_received_documents: next.show_received_documents ?? cfg?.show_received_documents ?? true,
        document_visibility_user_ids: next.document_visibility_user_ids ?? cfg?.document_visibility_user_ids ?? [],
        show_loading_photos: cfg?.show_loading_photos ?? true,
        loading_photos_visibility_user_ids: cfg?.loading_photos_visibility_user_ids ?? [],
      }, { headers: authHeaders });
      setCfg(data);
    } catch (err) {
      toast({ title: 'Save failed', description: err?.response?.data?.detail || err?.message });
    } finally { setBusy(false); }
  };

  if (!cfg) return null;
  const allowSet = new Set(cfg.document_visibility_user_ids || []);
  const globalOn = !!cfg.show_received_documents;

  const toggleUser = (id) => {
    const next = new Set(allowSet);
    if (next.has(id)) next.delete(id); else next.add(id);
    save({ document_visibility_user_ids: Array.from(next) });
  };

  const setAll = (mode) => {
    if (mode === 'all') {
      // Empty list = everyone can see (backwards compat).
      save({ document_visibility_user_ids: [] });
    } else {
      // Explicit list of every branch id = same effect as empty, but
      // documents future admin toggling of one user off. Prefer the
      // empty-list shortcut, but ship "none" as an all-off state.
      save({ document_visibility_user_ids: [] });
    }
  };

  const setNoneOverride = () => {
    // Non-empty list containing an impossible id blocks every real
    // branch user without changing the global toggle.
    save({ document_visibility_user_ids: ['__none__'] });
  };

  const restrictedActive = allowSet.size > 0 && !allowSet.has('__none__');
  const blockedAll = allowSet.has('__none__');

  return (
    <section
      className="rounded-2xl bg-white border border-neutral-200 p-5 mb-6"
      data-testid="branch-document-visibility-card"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="text-sm font-bold text-[#0F172A]">Uploaded documents visibility</div>
          <p className="text-xs text-neutral-500 mt-1 max-w-xl leading-relaxed">
            Global switch controls whether ANY branch user sees the received-document panel on their orders. When ON, you can further restrict visibility to specific branch users. Admin always sees the document preview panel on Admin Orders.
          </p>
        </div>
        <label className="inline-flex items-center gap-2 cursor-pointer select-none" data-testid="branch-documents-global-row">
          <span className={`text-xs font-semibold ${globalOn ? 'text-emerald-700' : 'text-neutral-500'}`}>
            {globalOn ? 'Global: ON' : 'Global: OFF'}
          </span>
          <input
            type="checkbox"
            checked={globalOn}
            disabled={busy}
            onChange={(e) => save({ show_received_documents: e.target.checked })}
            className="sr-only peer"
            data-testid="branch-documents-global-toggle"
          />
          <span className="w-10 h-6 bg-neutral-200 rounded-full peer-checked:bg-[#0078D4] transition relative after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:w-5 after:h-5 after:bg-white after:rounded-full after:transition peer-checked:after:translate-x-4" />
        </label>
      </div>

      {globalOn && (
        <div className="mt-4 rounded-xl border border-neutral-200 bg-neutral-50 p-4">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
            <div className="text-xs font-semibold text-[#0F172A]">
              Who can see documents?
              {blockedAll ? (
                <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-red-50 text-red-700 border border-red-200 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider">Restricted: none</span>
              ) : restrictedActive ? (
                <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-800 border border-amber-200 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider">Restricted: {allowSet.size} user{allowSet.size === 1 ? '' : 's'}</span>
              ) : (
                <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider">All users</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setAll('all')}
                disabled={busy}
                className="text-[11px] font-semibold text-[#0078D4] hover:underline disabled:opacity-50"
                data-testid="branch-documents-select-all"
              >
                Everyone
              </button>
              <span className="text-neutral-300">|</span>
              <button
                type="button"
                onClick={setNoneOverride}
                disabled={busy}
                className="text-[11px] font-semibold text-neutral-500 hover:text-red-600 hover:underline disabled:opacity-50"
                data-testid="branch-documents-select-none"
              >
                None
              </button>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-64 overflow-y-auto">
            {branches.length === 0 && (
              <div className="text-xs text-neutral-400 col-span-full">No branch users yet — create one above.</div>
            )}
            {branches.map((b) => {
              // When list is empty → every branch is effectively checked
              // (backwards-compat "all"). When list is ['__none__'] →
              // every real branch is unchecked. Otherwise → membership.
              const checked = (!blockedAll && allowSet.size === 0) || allowSet.has(b.id);
              return (
                <label
                  key={b.id}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-xs transition ${
                    checked ? 'bg-white border-[#0078D4]/60 text-[#0F172A]' : 'bg-white border-neutral-200 text-neutral-500 hover:border-neutral-300'
                  }`}
                  data-testid={`branch-documents-user-${b.id}`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={busy}
                    onChange={() => toggleUser(b.id)}
                    className="rounded"
                  />
                  <span className="font-semibold truncate">{b.name}</span>
                  <span className="text-neutral-400 truncate">({b.username})</span>
                </label>
              );
            })}
          </div>
          <p className="text-[10px] text-neutral-400 mt-3 leading-snug">
            Tip: leave every user checked to grant access to everyone — this stays true even for new branches you add later.
          </p>
        </div>
      )}
    </section>
  );
};


// ---------------------------------------------------------------------
// Loading Pictures visibility panel (Feb 2026)
// ---------------------------------------------------------------------
// Mirror of DocumentVisibilityPanel above, but for the Loading
// Pictures section of a branch order. Controls the
// `show_loading_photos` + `loading_photos_visibility_user_ids` fields.
// Admin always sees loading pictures on the Admin Orders page
// regardless of this setting.
const LoadingPicturesVisibilityPanel = ({ branches }) => {
  const { authHeaders } = useAdminAuth();
  const { toast } = useToast();
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/branch-portal-settings`, { headers: authHeaders })
      .then(({ data }) => setCfg(data))
      .catch(() => setCfg({ show_loading_photos: true, loading_photos_visibility_user_ids: [] }));
  }, [authHeaders]);

  const save = async (next) => {
    setBusy(true);
    try {
      const { data } = await axios.put(`${API}/admin/branch-portal-settings`, {
        // Merge existing config with the change so we don't clobber
        // sibling settings (hidden_category_slugs, available_qty_visible,
        // and the received-documents visibility pair).
        hidden_category_slugs: cfg?.hidden_category_slugs || [],
        available_qty_visible: cfg?.available_qty_visible ?? true,
        show_received_documents: cfg?.show_received_documents ?? true,
        document_visibility_user_ids: cfg?.document_visibility_user_ids ?? [],
        show_loading_photos: next.show_loading_photos ?? cfg?.show_loading_photos ?? true,
        loading_photos_visibility_user_ids: next.loading_photos_visibility_user_ids ?? cfg?.loading_photos_visibility_user_ids ?? [],
      }, { headers: authHeaders });
      setCfg(data);
    } catch (err) {
      toast({ title: 'Save failed', description: err?.response?.data?.detail || err?.message });
    } finally { setBusy(false); }
  };

  if (!cfg) return null;
  const allowSet = new Set(cfg.loading_photos_visibility_user_ids || []);
  const globalOn = !!cfg.show_loading_photos;

  const toggleUser = (id) => {
    const next = new Set(allowSet);
    if (next.has(id)) next.delete(id); else next.add(id);
    save({ loading_photos_visibility_user_ids: Array.from(next) });
  };

  // Empty list = everyone can see (backwards compat).
  const setAll = () => save({ loading_photos_visibility_user_ids: [] });
  // Non-empty list containing an impossible id blocks every real
  // branch user without changing the global toggle.
  const setNoneOverride = () => save({ loading_photos_visibility_user_ids: ['__none__'] });

  const restrictedActive = allowSet.size > 0 && !allowSet.has('__none__');
  const blockedAll = allowSet.has('__none__');

  return (
    <section
      className="rounded-2xl bg-white border border-neutral-200 p-5 mb-6"
      data-testid="branch-loading-photos-visibility-card"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="text-sm font-bold text-[#0F172A]">Loading files visibility</div>
          <p className="text-xs text-neutral-500 mt-1 max-w-xl leading-relaxed">
            Global switch controls whether ANY branch user sees the Loading Pictures section on their orders. When ON, you can further restrict visibility to specific branch users. Admin always sees loading pictures on Admin Orders.
          </p>
        </div>
        <label className="inline-flex items-center gap-2 cursor-pointer select-none" data-testid="branch-loading-photos-global-row">
          <span className={`text-xs font-semibold ${globalOn ? 'text-emerald-700' : 'text-neutral-500'}`}>
            {globalOn ? 'Global: ON' : 'Global: OFF'}
          </span>
          <input
            type="checkbox"
            checked={globalOn}
            disabled={busy}
            onChange={(e) => save({ show_loading_photos: e.target.checked })}
            className="sr-only peer"
            data-testid="branch-loading-photos-global-toggle"
          />
          <span className="w-10 h-6 bg-neutral-200 rounded-full peer-checked:bg-[#0078D4] transition relative after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:w-5 after:h-5 after:bg-white after:rounded-full after:transition peer-checked:after:translate-x-4" />
        </label>
      </div>

      {globalOn && (
        <div className="mt-4 rounded-xl border border-neutral-200 bg-neutral-50 p-4">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
            <div className="text-xs font-semibold text-[#0F172A]">
              Who can see loading pictures?
              {blockedAll ? (
                <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-red-50 text-red-700 border border-red-200 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider">Restricted: none</span>
              ) : restrictedActive ? (
                <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-800 border border-amber-200 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider">Restricted: {allowSet.size} user{allowSet.size === 1 ? '' : 's'}</span>
              ) : (
                <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider">All users</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={setAll}
                disabled={busy}
                className="text-[11px] font-semibold text-[#0078D4] hover:underline disabled:opacity-50"
                data-testid="branch-loading-photos-select-all"
              >
                Everyone
              </button>
              <span className="text-neutral-300">|</span>
              <button
                type="button"
                onClick={setNoneOverride}
                disabled={busy}
                className="text-[11px] font-semibold text-neutral-500 hover:text-red-600 hover:underline disabled:opacity-50"
                data-testid="branch-loading-photos-select-none"
              >
                None
              </button>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-64 overflow-y-auto">
            {branches.length === 0 && (
              <div className="text-xs text-neutral-400 col-span-full">No branch users yet — create one above.</div>
            )}
            {branches.map((b) => {
              const checked = (!blockedAll && allowSet.size === 0) || allowSet.has(b.id);
              return (
                <label
                  key={b.id}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-xs transition ${
                    checked ? 'bg-white border-[#0078D4]/60 text-[#0F172A]' : 'bg-white border-neutral-200 text-neutral-500 hover:border-neutral-300'
                  }`}
                  data-testid={`branch-loading-photos-user-${b.id}`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={busy}
                    onChange={() => toggleUser(b.id)}
                    className="rounded"
                  />
                  <span className="font-semibold truncate">{b.name}</span>
                  <span className="text-neutral-400 truncate">({b.username})</span>
                </label>
              );
            })}
          </div>
          <p className="text-[10px] text-neutral-400 mt-3 leading-snug">
            Tip: leave every user checked to grant access to everyone — this stays true even for new branches you add later.
          </p>
        </div>
      )}
    </section>
  );
};


const AdminBranches = () => {
  const { authHeaders } = useAdminAuth();
  const { toast } = useToast();
  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // { ...form } | null
  const [busy, setBusy] = useState(false);
  // Branch-portal favicon — separate from the storefront/driver favicon
  // generator; just a URL to an uploaded image.
  const [favicon, setFavicon] = useState('');
  const [faviconBusy, setFaviconBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: rowsData }, { data: catsData }, { data: settingsData }, { data: whData }] = await Promise.all([
        axios.get(`${API}/admin/branches`, { headers: authHeaders }),
        axios.get(`${API}/admin/categories`, { headers: authHeaders }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/settings`, { headers: authHeaders }).catch(() => ({ data: {} })),
        axios.get(`${API}/admin/warehouses`, { headers: authHeaders }).catch(() => ({ data: { warehouses: [] } })),
      ]);
      setRows(rowsData || []);
      setCategories(catsData || []);
      setWarehouses(Array.isArray(whData?.warehouses) ? whData.warehouses : []);
      setFavicon(settingsData?.branch_portal_favicon_url || '');
    } catch (err) {
      toast({ title: 'Failed to load branches', description: err?.response?.data?.detail || err?.message });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const saveFavicon = async (nextUrl) => {
    setFaviconBusy(true);
    try {
      // Fetch the FULL settings doc, patch just the favicon field, PUT
      // back. The endpoint replaces the doc — we mustn't lose the other
      // hundred+ fields on the way.
      const { data: current } = await axios.get(`${API}/admin/settings`, { headers: authHeaders });
      const body = { ...current, branch_portal_favicon_url: nextUrl || '' };
      await axios.put(`${API}/admin/settings`, body, { headers: authHeaders });
      setFavicon(nextUrl || '');
      toast({ title: 'Branch portal favicon saved' });
    } catch (err) {
      toast({ title: 'Save failed', description: err?.response?.data?.detail || err?.message });
    } finally { setFaviconBusy(false); }
  };

  const openCreate = () => setEditing({ ...empty });
  const openEdit = (b) => setEditing({
    id: b.id,
    username: b.username,
    name: b.name,
    emails: Array.isArray(b.email) ? [...b.email] : (b.email ? [String(b.email)] : []),
    emailDraft: '',
    password: '',
    customer_numbers_text: (b.customer_numbers || []).join(', '),
    allowed_category_slugs: [...(b.allowed_category_slugs || [])],
    active: b.active,
    duty_start: b.duty_start || '',
    duty_end: b.duty_end || '',
    duty_days: Array.isArray(b.duty_days) ? [...b.duty_days] : [],
    off_duty_days: Array.isArray(b.off_duty_days) ? [...b.off_duty_days] : [],
    duty_schedule: deriveDutySchedule(b),
    warehouse_id: b.warehouse_id || '',
    has_receiving: Boolean(b.has_receiving),
  });
  const close = () => setEditing(null);

  const parseCnums = (text) =>
    (text || '').split(/[,\s\n]+/).map((s) => s.trim()).filter(Boolean);

  const toggleCategory = (slug) => {
    setEditing((prev) => {
      if (!prev) return prev;
      const cur = new Set(prev.allowed_category_slugs || []);
      if (cur.has(slug)) cur.delete(slug); else cur.add(slug);
      return { ...prev, allowed_category_slugs: Array.from(cur) };
    });
  };

  const save = async () => {
    if (!editing) return;
    if (!editing.id && (!editing.username.trim() || !editing.password)) {
      toast({ title: 'Username and temporary password required' });
      return;
    }
    if (!editing.id && editing.password.length < 8) {
      toast({ title: 'Password must be at least 8 characters' });
      return;
    }
    setBusy(true);
    try {
      // Fold any lingering emailDraft (unsubmitted chip) into the list
      // so admins don't lose text they typed but forgot to Enter.
      const emails = [
        ...(editing.emails || []),
        ...parseEmails(editing.emailDraft),
      ].filter((v, i, a) => a.indexOf(v) === i);
      const bad = emails.find((e) => !isValidEmail(e));
      if (bad) {
        toast({ title: 'Invalid email', description: bad });
        setBusy(false);
        return;
      }
      const body = {
        name: editing.name || editing.username,
        email: emails,
        customer_numbers: parseCnums(editing.customer_numbers_text),
        allowed_category_slugs: editing.allowed_category_slugs || [],
        active: editing.active,
        duty_start: editing.duty_start || '',
        duty_end: editing.duty_end || '',
        duty_days: Array.isArray(editing.duty_days) ? editing.duty_days : [],
        off_duty_days: Array.isArray(editing.off_duty_days) ? editing.off_duty_days : [],
        // Feb 2026 — Per-weekday schedule (backend uses this as the
        // source of truth; legacy fields are kept for middleware
        // that may still read them).
        duty_schedule: Array.isArray(editing.duty_schedule) && editing.duty_schedule.length === 7
          ? editing.duty_schedule.map((d) => ({
              start: (d?.start || '').trim(),
              end: (d?.end || '').trim(),
              off: !!d?.off,
            }))
          : [],
        warehouse_id: editing.warehouse_id || '',
        has_receiving: Boolean(editing.has_receiving),
      };
      if (editing.password) body.password = editing.password;
      if (editing.id) {
        await axios.put(`${API}/admin/branches/${editing.id}`, body, { headers: authHeaders });
        toast({ title: 'Branch updated' });
      } else {
        await axios.post(`${API}/admin/branches`, { ...body, username: editing.username.trim() }, { headers: authHeaders });
        toast({ title: 'Branch created', description: `${editing.username} will be forced to change the temporary password on first sign-in.` });
      }
      close();
      load();
    } catch (err) {
      toast({ title: 'Save failed', description: err?.response?.data?.detail || err?.message });
    } finally { setBusy(false); }
  };

  const remove = async (b) => {
    // eslint-disable-next-line no-restricted-globals, no-alert
    if (!confirm(`Delete branch "${b.name || b.username}"? They will lose portal access immediately.`)) return;
    try {
      await axios.delete(`${API}/admin/branches/${b.id}`, { headers: authHeaders });
      toast({ title: 'Branch deleted' });
      load();
    } catch (err) {
      toast({ title: 'Delete failed', description: err?.response?.data?.detail || err?.message });
    }
  };

  const copyLoginUrl = () => {
    const url = `${window.location.origin}/branch/login`;
    navigator.clipboard.writeText(url).then(
      () => toast({ title: 'Branch login URL copied', description: url }),
      () => toast({ title: 'Copy failed' }),
    );
  };

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto px-5 lg:px-8 py-8">
        <div className="flex items-start justify-between mb-6 gap-3 flex-wrap">
          <div>
            <h1 className="font-display text-2xl font-bold text-[#0F172A] flex items-center gap-2">
              <Building2 className="w-6 h-6 text-[#0078D4]" /> Branches
            </h1>
            <p className="text-neutral-500 mt-1 text-sm">
              Create branch portal logins. Each branch is scoped to one or more customer_numbers — they&apos;ll see only orders that carry those numbers on <code className="text-xs bg-neutral-100 px-1 rounded">/branch</code>.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={copyLoginUrl}
              className="rounded-full border border-neutral-300 text-neutral-700 px-3 py-2 text-xs font-bold inline-flex items-center gap-1.5 hover:bg-neutral-50"
              data-testid="admin-branches-copy-url"
            >
              <Copy className="w-3.5 h-3.5" /> Copy /branch/login URL
            </button>
            <button
              type="button"
              onClick={openCreate}
              className="rounded-full bg-[#0F172A] text-white px-4 py-2 text-xs font-bold inline-flex items-center gap-1.5"
              data-testid="admin-branches-create-btn"
            >
              <Plus className="w-4 h-4" /> New branch
            </button>
          </div>
        </div>

        {/* Branch portal favicon — dedicated slot, separate from the
            storefront/driver favicon generator. Applies on /branch/login,
            New Order, My Orders, and the /track page when opened by a
            signed-in branch. */}
        <div className="rounded-2xl bg-white border border-neutral-200 p-5 mb-6" data-testid="branch-favicon-card">
          <div className="flex items-start gap-4 flex-wrap">
            <div className="flex items-center gap-2 min-w-0">
              <ImageIcon className="w-5 h-5 text-[#0078D4]" />
              <div>
                <div className="text-sm font-bold text-[#0F172A]">Branch Portal favicon</div>
                <div className="text-xs text-neutral-500 mt-0.5">
                  Displayed on every <code className="text-[10px] bg-neutral-100 px-1 rounded">/branch/*</code> page and on the tracking page when a branch is signed in. Upload a square PNG/SVG (256&times;256 recommended).
                </div>
              </div>
            </div>
            <div className="ml-auto flex items-center gap-3">
              {favicon && (
                <div className="w-12 h-12 rounded-lg border border-neutral-200 bg-neutral-50 flex items-center justify-center overflow-hidden">
                  <img src={resolveImageUrl(favicon)} alt="Branch favicon" className="max-w-full max-h-full object-contain" />
                </div>
              )}
            </div>
          </div>
          <div className="mt-4 max-w-md">
            <ImageUpload
              value={favicon}
              onChange={(v) => saveFavicon(v)}
              testId="branch-favicon-upload"
            />
            {favicon && (
              <button
                type="button"
                onClick={() => saveFavicon('')}
                disabled={faviconBusy}
                className="mt-2 text-[11px] font-semibold text-red-500 hover:text-red-700 disabled:opacity-50"
                data-testid="branch-favicon-clear"
              >
                Remove custom favicon
              </button>
            )}
          </div>
        </div>

        {/* Feb 2026 — Document visibility policy for /branch orders.
            Global toggle + per-user allow-list. Super admin can always
            see the upload panel on Admin Orders regardless of these
            settings. */}
        <DocumentVisibilityPanel branches={rows} />
        {/* Feb 2026 — parallel policy for Loading Pictures. Same UX;
            controls the `show_loading_photos` + allow-list flags. */}
        <LoadingPicturesVisibilityPanel branches={rows} />



        {loading ? (
          <div className="rounded-2xl bg-white border border-neutral-200 p-8 flex items-center gap-2 text-sm text-neutral-500">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="rounded-2xl bg-white border border-neutral-200 p-10 text-center">
            <Building2 className="w-10 h-10 text-neutral-300 mx-auto mb-3" />
            <div className="text-sm text-neutral-500">No branches yet — create the first one to enable the /branch portal.</div>
          </div>
        ) : (
          <>
            {/* Mobile card list — Feb 2026 native-app pass */}
            <div className="md:hidden space-y-2" data-testid="admin-branches-list-mobile">
              {rows.map((b) => (
                <MobileListCard
                  key={b.id}
                  testId={`admin-branch-card-${b.id}`}
                  title={b.name}
                  statusPill={
                    <span
                      className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                        b.active
                          ? 'bg-emerald-50 border border-emerald-200 text-emerald-700'
                          : 'bg-neutral-100 border border-neutral-200 text-neutral-500'
                      }`}
                    >
                      {b.active ? 'Active' : 'Disabled'}
                    </span>
                  }
                  businessName={b.username ? `@${b.username}` : '—'}
                  customerName={
                    (b.customer_numbers || []).length === 0
                      ? 'Not linked'
                      : `${(b.customer_numbers || []).length} cust #`
                  }
                  amount={
                    (b.allowed_category_slugs || []).length === 0
                      ? 'All categories'
                      : `${(b.allowed_category_slugs || []).length} category`
                  }
                  date={b.last_login_at ? b.last_login_at.replace('T', ' ').slice(0, 16) : 'Never signed in'}
                  onClick={() => openEdit(b)}
                  actions={[
                    { label: 'Edit', onClick: () => openEdit(b), testId: `admin-branch-card-edit-${b.id}` },
                    { divider: true },
                    { label: 'Delete', onClick: () => remove(b), danger: true, testId: `admin-branch-card-delete-${b.id}` },
                  ]}
                />
              ))}
            </div>

            <div className="hidden md:block rounded-2xl bg-white border border-neutral-200 overflow-hidden">
            <table className="w-full text-sm" data-testid="admin-branches-table">
              <thead className="bg-neutral-50 text-neutral-500 text-[10px] uppercase tracking-wider font-bold">
                <tr>
                  <th className="text-left p-3">Branch</th>
                  <th className="text-left p-3">Username</th>
                  <th className="text-left p-3">Customer numbers</th>
                  <th className="text-left p-3">Categories</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-right p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((b) => (
                  <tr key={b.id} className="border-t border-neutral-100" data-testid={`admin-branch-row-${b.id}`}>
                    <td className="p-3 font-semibold text-[#0F172A]">
                      {b.name}
                      {(() => {
                        const emails = Array.isArray(b.email) ? b.email : (b.email ? [String(b.email)] : []);
                        if (!emails.length) return null;
                        return (
                          <div className="text-[10px] text-neutral-400 font-normal font-mono mt-0.5" data-testid={`admin-branch-emails-${b.id}`}>
                            {emails.join(', ')}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="p-3 font-mono text-xs text-neutral-600">{b.username}</td>
                    <td className="p-3">
                      {(b.customer_numbers || []).length === 0 ? (
                        <span className="text-xs text-amber-600 font-semibold">Not linked — sees 0 orders</span>
                      ) : (
                        <div className="flex gap-1 flex-wrap">
                          {b.customer_numbers.map((c) => (
                            <span key={c} className="px-2 py-0.5 rounded-full bg-blue-50 border border-blue-200 text-blue-800 text-[10px] font-mono">
                              {c}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="p-3">
                      {(b.allowed_category_slugs || []).length === 0 ? (
                        <span className="text-xs text-neutral-500 italic">All categories</span>
                      ) : (
                        <div className="flex gap-1 flex-wrap max-w-[220px]">
                          {b.allowed_category_slugs.map((s) => (
                            <span key={s} className="px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-[10px] font-mono">
                              {s}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="p-3">
                      {b.active ? (
                        <span className="text-xs text-emerald-700 font-semibold">Active</span>
                      ) : (
                        <span className="text-xs text-neutral-400 font-semibold">Disabled</span>
                      )}
                      {b.must_change_password && (
                        <div className="text-[10px] text-amber-600 mt-0.5">Must change password</div>
                      )}
                      {b.last_login_at && (
                        <div className="text-[10px] text-neutral-400 mt-0.5 font-mono">last login: {b.last_login_at.replace('T', ' ').slice(0, 19)}</div>
                      )}
                    </td>
                    <td className="p-3 text-right">
                      <button
                        type="button"
                        onClick={() => openEdit(b)}
                        className="inline-flex items-center gap-1 text-xs font-bold text-[#0078D4] hover:text-[#005A9E] mr-3"
                        data-testid={`admin-branch-edit-${b.id}`}
                      >
                        <Edit3 className="w-3.5 h-3.5" /> Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => remove(b)}
                        className="inline-flex items-center gap-1 text-xs font-bold text-red-500 hover:text-red-700"
                        data-testid={`admin-branch-delete-${b.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" /> Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-neutral-100 sticky top-0 bg-white z-10">
              <h2 className="font-display text-lg font-bold text-[#0F172A]">{editing.id ? 'Edit branch' : 'New branch'}</h2>
              <button type="button" onClick={close} className="text-neutral-500 hover:text-neutral-900"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label htmlFor="br-name" className="block text-[10px] font-bold tracking-wider text-neutral-500 uppercase mb-1">Branch name</label>
                <Input id="br-name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="e.g. MRP Barka Branch" className="text-sm" data-testid="admin-branch-modal-name" />
              </div>
              <div>
                <label htmlFor="br-email" className="block text-[10px] font-bold tracking-wider text-neutral-500 uppercase mb-1">Branch emails (optional)</label>
                <div
                  className="flex flex-wrap gap-1.5 rounded-md border border-neutral-300 bg-white px-2 py-1.5 focus-within:border-[#0078D4] focus-within:ring-2 focus-within:ring-[#0078D4]/20"
                  onClick={() => document.getElementById('br-email')?.focus()}
                  data-testid="admin-branch-modal-emails-chips"
                >
                  {(editing.emails || []).map((e, i) => (
                    <span
                      key={`${e}-${i}`}
                      className="inline-flex items-center gap-1 rounded-full bg-[#EFF6FF] border border-[#BFDBFE] px-2 py-0.5 text-[11px] font-medium text-[#1D4ED8]"
                      data-testid={`admin-branch-email-chip-${i}`}
                    >
                      {e}
                      <button
                        type="button"
                        onClick={(ev) => {
                          ev.stopPropagation();
                          setEditing({
                            ...editing,
                            emails: editing.emails.filter((_, idx) => idx !== i),
                          });
                        }}
                        className="text-[#1D4ED8]/60 hover:text-[#1D4ED8]"
                        aria-label={`Remove ${e}`}
                        data-testid={`admin-branch-email-chip-remove-${i}`}
                      >×</button>
                    </span>
                  ))}
                  <input
                    id="br-email"
                    type="text"
                    value={editing.emailDraft || ''}
                    placeholder={editing.emails?.length ? 'Add another…' : 'e.g. barka@mrpco.net, ops@mrpco.net'}
                    className="flex-1 min-w-[150px] outline-none border-0 bg-transparent text-sm px-1 py-0.5"
                    data-testid="admin-branch-modal-email"
                    onChange={(e) => {
                      // Auto-commit when comma / semicolon / space typed.
                      const val = e.target.value;
                      if (/[,;\s]$/.test(val)) {
                        const next = parseEmails(val);
                        const merged = [...(editing.emails || []), ...next]
                          .filter((v, i, a) => a.indexOf(v) === i);
                        setEditing({ ...editing, emails: merged, emailDraft: '' });
                      } else {
                        setEditing({ ...editing, emailDraft: val });
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === 'Tab') {
                        const next = parseEmails(editing.emailDraft);
                        if (next.length) {
                          e.preventDefault();
                          const merged = [...(editing.emails || []), ...next]
                            .filter((v, i, a) => a.indexOf(v) === i);
                          setEditing({ ...editing, emails: merged, emailDraft: '' });
                        }
                      } else if (e.key === 'Backspace' && !editing.emailDraft && editing.emails?.length) {
                        setEditing({ ...editing, emails: editing.emails.slice(0, -1) });
                      }
                    }}
                    onBlur={() => {
                      const next = parseEmails(editing.emailDraft);
                      if (next.length) {
                        const merged = [...(editing.emails || []), ...next]
                          .filter((v, i, a) => a.indexOf(v) === i);
                        setEditing({ ...editing, emails: merged, emailDraft: '' });
                      }
                    }}
                  />
                </div>
                <p className="text-[10px] text-neutral-400 mt-1">Comma / semicolon / space separated · press Enter to add. Order status emails (received, out for delivery, delivered) will be sent to every address in this list.</p>
              </div>
              <div>
                <label htmlFor="br-user" className="block text-[10px] font-bold tracking-wider text-neutral-500 uppercase mb-1">Username (sign-in)</label>
                <Input id="br-user" value={editing.username} onChange={(e) => setEditing({ ...editing, username: e.target.value })} placeholder="e.g. barka" disabled={!!editing.id} className="text-sm font-mono disabled:bg-neutral-100" data-testid="admin-branch-modal-username" />
                {editing.id && <p className="text-[10px] text-neutral-400 mt-1">Username can&apos;t be changed after creation.</p>}
              </div>
              <div>
                <label htmlFor="br-pwd" className="block text-[10px] font-bold tracking-wider text-neutral-500 uppercase mb-1">
                  {editing.id ? 'Reset password (optional)' : 'Temporary password'}
                </label>
                <div className="relative">
                  <Input id="br-pwd" type="text" value={editing.password} onChange={(e) => setEditing({ ...editing, password: e.target.value })} placeholder={editing.id ? 'Leave blank to keep current' : 'Min 8 characters'} className="text-sm font-mono pr-10" data-testid="admin-branch-modal-password" />
                  <KeyRound className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                </div>
                <p className="text-[10px] text-neutral-400 mt-1">User is forced to change this on first sign-in.</p>
              </div>
              <div>
                <label htmlFor="br-cnums" className="block text-[10px] font-bold tracking-wider text-neutral-500 uppercase mb-1">Customer numbers</label>
                <textarea
                  id="br-cnums"
                  value={editing.customer_numbers_text}
                  onChange={(e) => setEditing({ ...editing, customer_numbers_text: e.target.value })}
                  placeholder="Comma / newline separated, e.g. 100010, 100048"
                  rows={3}
                  className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#0078D4]/40"
                  data-testid="admin-branch-modal-cnums"
                />
                <p className="text-[11px] text-neutral-500 mt-1">
                  The branch will see every order tagged with any of these customer numbers.
                  <span className="font-semibold"> Empty list = branch sees 0 orders.</span>
                </p>
              </div>
              <div>
                <label className="block text-[10px] font-bold tracking-wider text-neutral-500 uppercase mb-1 flex items-center gap-1.5">
                  <LayoutGrid className="w-3.5 h-3.5" /> Allowed categories
                </label>
                <div className="rounded-lg border border-neutral-200 bg-neutral-50/40 p-3" data-testid="admin-branch-modal-categories">
                  {categories.length === 0 ? (
                    <p className="text-[11px] text-neutral-400 italic">No categories yet. Create one on Products &gt; Categories first.</p>
                  ) : (
                    <>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 max-h-56 overflow-y-auto pr-1">
                        {categories.map((c) => {
                          const checked = (editing.allowed_category_slugs || []).includes(c.slug);
                          return (
                            <label
                              key={c.slug}
                              className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md border cursor-pointer transition ${
                                checked
                                  ? 'bg-emerald-50 border-emerald-200'
                                  : 'bg-white border-neutral-200 hover:border-neutral-300'
                              }`}
                              data-testid={`admin-branch-cat-${c.slug}`}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleCategory(c.slug)}
                                className="w-4 h-4 accent-emerald-500"
                              />
                              <span className={`text-sm truncate ${checked ? 'text-emerald-800 font-semibold' : 'text-[#0F172A]'}`}>
                                {c.name}
                              </span>
                              <span className="text-[10px] text-neutral-400 ml-auto font-mono">{c.slug}</span>
                            </label>
                          );
                        })}
                      </div>
                      <div className="mt-3 flex items-center gap-2 flex-wrap">
                        <button
                          type="button"
                          onClick={() => setEditing({ ...editing, allowed_category_slugs: categories.map((c) => c.slug) })}
                          className="text-[11px] font-semibold text-[#0078D4] hover:underline"
                          data-testid="admin-branch-cat-select-all"
                        >
                          Select all
                        </button>
                        <span className="text-neutral-300">·</span>
                        <button
                          type="button"
                          onClick={() => setEditing({ ...editing, allowed_category_slugs: [] })}
                          className="text-[11px] font-semibold text-neutral-500 hover:text-red-600 hover:underline"
                          data-testid="admin-branch-cat-clear"
                        >
                          Clear
                        </button>
                        <span className="ml-auto text-[10px] text-neutral-500">
                          {(editing.allowed_category_slugs || []).length === 0 ? (
                            <span className="italic">Empty = sees ALL categories</span>
                          ) : (
                            <>
                              <span className="font-bold text-neutral-700">{editing.allowed_category_slugs.length}</span> of {categories.length} selected
                            </>
                          )}
                        </span>
                      </div>
                    </>
                  )}
                </div>
                <p className="text-[11px] text-neutral-500 mt-1">
                  This branch will only see the ticked categories on <code className="text-[10px] bg-neutral-100 px-1 rounded">/branch</code>. Leave empty to show every visible category.
                </p>
              </div>
              <label className="flex items-center gap-2 px-3 py-2 rounded-lg border border-neutral-200 cursor-pointer">
                <input
                  type="checkbox"
                  checked={editing.active}
                  onChange={(e) => setEditing({ ...editing, active: e.target.checked })}
                  className="w-4 h-4 accent-[#0078D4]"
                  data-testid="admin-branch-modal-active"
                />
                <span className="text-sm text-[#0F172A]">Active — uncheck to revoke portal access without deleting</span>
              </label>
              <div>
                <label htmlFor="br-warehouse" className="block text-[10px] font-bold tracking-wider text-neutral-500 uppercase mb-1">
                  Assigned warehouse (Branch FEFO)
                </label>
                <select
                  id="br-warehouse"
                  value={editing.warehouse_id || ''}
                  onChange={(e) => setEditing({ ...editing, warehouse_id: e.target.value })}
                  className="w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0078D4]/40"
                  data-testid="admin-branch-modal-warehouse"
                >
                  <option value="">— Not assigned —</option>
                  {warehouses.map((w) => (
                    <option key={w.id} value={w.id}>{w.name}</option>
                  ))}
                </select>
                <p className="text-[11px] text-neutral-500 mt-1">
                  Binds this branch to a warehouse. Auto-ASNs on order receipt and Branch Receiving views are scoped to this warehouse.
                </p>
              </div>
              <label className="flex items-center gap-2 px-3 py-2 rounded-lg border border-neutral-200 cursor-pointer">
                <input
                  type="checkbox"
                  checked={Boolean(editing.has_receiving)}
                  onChange={(e) => setEditing({ ...editing, has_receiving: e.target.checked })}
                  disabled={!editing.warehouse_id}
                  className="w-4 h-4 accent-[#0078D4]"
                  data-testid="admin-branch-modal-has-receiving"
                />
                <span className="text-sm text-[#0F172A]">
                  Enable Receiving — surfaces the <b>Receiving</b> and <b>Live Stock</b> tabs on the branch portal.
                  {!editing.warehouse_id && (
                    <span className="block text-[11px] text-neutral-500 mt-0.5">Assign a warehouse first to enable this option.</span>
                  )}
                </span>
              </label>
              <div className="rounded-lg border border-neutral-200 bg-neutral-50/40 p-3" data-testid="admin-branch-duty-block">
                <div className="flex items-baseline justify-between gap-2 mb-2">
                  <div className="text-[10px] font-bold tracking-wider text-neutral-500 uppercase">Weekly duty schedule</div>
                  <div className="text-[10px] font-normal text-neutral-500">Mon → Sun · UTC+4</div>
                </div>
                <div className="rounded-lg border border-neutral-200 overflow-hidden bg-white">
                  <div className="grid grid-cols-[80px_60px_1fr_1fr] text-[10px] font-semibold uppercase tracking-wide text-neutral-500 bg-neutral-50/70 border-b border-neutral-200">
                    <div className="px-2 py-1.5">Day</div>
                    <div className="px-2 py-1.5 text-center">Off</div>
                    <div className="px-2 py-1.5">Start</div>
                    <div className="px-2 py-1.5">End</div>
                  </div>
                  {WEEKDAY_META.map((d) => {
                    const dayRow = (Array.isArray(editing.duty_schedule) && editing.duty_schedule[d.v])
                      || { ...BLANK_DAY };
                    const isOff = !!dayRow.off;
                    const updateDay = (patch) => {
                      setEditing((prev) => {
                        if (!prev) return prev;
                        const next = Array.isArray(prev.duty_schedule) && prev.duty_schedule.length === 7
                          ? prev.duty_schedule.map((e) => ({ ...e }))
                          : blankWeek();
                        next[d.v] = { ...next[d.v], ...patch };
                        return { ...prev, duty_schedule: next };
                      });
                    };
                    return (
                      <div
                        key={d.v}
                        className={`grid grid-cols-[80px_60px_1fr_1fr] items-center border-t border-neutral-100 ${
                          isOff ? 'bg-amber-50/40' : 'bg-white'
                        }`}
                        data-testid={`admin-branch-duty-row-${d.v}`}
                      >
                        <div className="px-2 py-1.5 text-xs font-semibold text-neutral-700">
                          {d.l}
                        </div>
                        <div className="px-2 py-1.5 flex justify-center">
                          <input
                            type="checkbox"
                            checked={isOff}
                            onChange={(e) => updateDay({
                              off: e.target.checked,
                              ...(e.target.checked ? { start: '', end: '' } : {}),
                            })}
                            className="w-4 h-4 accent-amber-500 cursor-pointer"
                            data-testid={`admin-branch-duty-off-${d.v}`}
                          />
                        </div>
                        <div className="px-2 py-1.5">
                          <input
                            type="time"
                            value={dayRow.start || ''}
                            disabled={isOff}
                            onChange={(e) => updateDay({ start: e.target.value })}
                            className={`w-full h-8 px-2 rounded-md border text-xs tabular-nums focus:border-sky-500 focus:outline-none ${
                              isOff ? 'bg-neutral-100 text-neutral-400 border-neutral-200 cursor-not-allowed' : 'border-neutral-300'
                            }`}
                            data-testid={`admin-branch-duty-start-${d.v}`}
                          />
                        </div>
                        <div className="px-2 py-1.5">
                          <input
                            type="time"
                            value={dayRow.end || ''}
                            disabled={isOff}
                            onChange={(e) => updateDay({ end: e.target.value })}
                            className={`w-full h-8 px-2 rounded-md border text-xs tabular-nums focus:border-sky-500 focus:outline-none ${
                              isOff ? 'bg-neutral-100 text-neutral-400 border-neutral-200 cursor-not-allowed' : 'border-neutral-300'
                            }`}
                            data-testid={`admin-branch-duty-end-${d.v}`}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
                <p className="text-[11px] text-neutral-500 mt-2">
                  Mark a day as <span className="font-semibold text-amber-700">Off</span> to lock the whole day. Leave both times blank on an on-duty day for 24/7 access.
                </p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 p-5 border-t border-neutral-100 sticky bottom-0 bg-white">
              <Button type="button" onClick={close} variant="outline" className="rounded-full h-9"><RotateCcw className="w-4 h-4 mr-1" /> Cancel</Button>
              <Button type="button" onClick={save} disabled={busy} className="rounded-full bg-[#0F172A] hover:bg-black text-white h-9" data-testid="admin-branch-modal-save">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" /> {editing.id ? 'Save changes' : 'Create branch'}</>}
              </Button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminBranches;
